import SurveyBaseController from './survey_base_controller';
import SlimSelect from 'slim-select';

export default class extends SurveyBaseController {
  static values = {
    surveyableType: String,
    surveyableId: String,
    mode: String,
    gridCols: Number,
    refresh: Boolean,
    showControls: { type: Boolean, default: true },
    refreshInterval: { type: Number, default: 100000 }, // Default to 10 seconds
    period: { type: String, default: "today" }
  }

  static targets = ["periodSelect", "resultCount", "filterBar", "stateFilter", "taskerFilter", "placeOwnerFilter", "dayFilter", "dateRange", "dateFrom", "dateTo", "error", "refreshIcon"]

  static PERIOD_COOKIE = "surveys_dashboard_period"
  static PERIOD_COOKIE_TTL_HOURS = 24

  async connect() {
    super.connect();
    this.survey = this.initializeSurvey(this.modeValue);
    this.vizPanelOptions = {
      allowHideQuestions: false,
      allowHideEmptyQuestions: true,
      allowShowMissingAnswers: true,
      allowShowPercentages: true,
      allowSortAnswers: true,
      allowDragDrop: false,
      allowHideEmptyValues: true,
      allowSelection: true,
      haveCommercialLicense: true,
      labelTruncateLength: 100
    };

    SurveyAnalytics.VisualizationManager.unregisterVisualizerForAll(SurveyAnalytics.WordCloud);

    this.allResults = [];
    this.surveyResults = [];
    this.restoreFromUrlParams();

    // Card #1927 -- do not fetch until this dashboard is actually on screen.
    //
    // This controller's element is server-rendered inline in the mission
    // show page's Results panel, which means connect() fires on every
    // mission page load whichever tab the Mission Controller landed on.
    // The fetch below is NOT cheap at every period: the endpoint serialises
    // every matching response into the JSON payload, so period=all on a
    // ~670-response mission is 267 ms and 814 KB against period=today's
    // 18 ms and 10 KB, and SurveyAnalytics then has to render all of it.
    //
    // Period is sticky in a 24-hour cookie (PERIOD_COOKIE, set by
    // periodChanged), so one switch to "All Time" makes every subsequent
    // mission page load pay that cost for the rest of the day -- including
    // page loads where nobody ever looks at the Results tab. Deferring the
    // fetch is what stops that.
    //
    // An IntersectionObserver rather than a synchronous visibility check
    // on purpose: at connect() time persist_tabs_controller.js may not have
    // restored the active tab yet (Stimulus gives no ordering guarantee
    // between the two controllers), so "is my panel showing" is not yet
    // answerable. The observer fires as soon as the browser knows, in
    // either direction. A `display: none` panel has no box and never
    // intersects at any rootMargin, which is exactly the discriminator
    // wanted -- while the generous margin means a merely below-the-fold
    // dashboard on the active tab still loads immediately rather than
    // waiting for a scroll.
    this.visibilityObserver = new IntersectionObserver((entries) => {
      if (!entries.some((entry) => entry.isIntersecting)) return;
      this.disconnectVisibilityObserver();
      this.loadAndRender();
    }, { rootMargin: "2000px" });
    this.visibilityObserver.observe(this.element);
  }

  disconnectVisibilityObserver() {
    if (!this.visibilityObserver) return;
    this.visibilityObserver.disconnect();
    this.visibilityObserver = null;
  }

  // Everything connect() used to do inline once the survey scaffolding was
  // in place (card #1927). Guarded so a second visibility event cannot kick
  // off a duplicate fetch.
  async loadAndRender() {
    if (this.hasRendered) return;
    this.hasRendered = true;

    try {
      this.showLoading();
      const surveyData = await this.loadSurveyResults();
      this.hideError();
      this.survey.fromJSON(surveyData.survey);
      this.allResults = surveyData.results;
      this.populateFilterOptions();
      this.applyAttributeFilters();
      this.updateUrlParams();

      this.renderVizPanel();
      this.setupAutoRefresh();
    } catch (error) {
      console.error("Failed to load survey results:", error);
      this.showError();
    } finally {
      this.hideLoading();
    }

    this.applyGridColumns();
    // Card #3648 -- after the grid's column count is settled, not before:
    // resizePlots measures against the container the cards actually ended up in.
    this.resizePlots();
    this.observePlotResizes();

    if (this.surveyableTypeValue == "Mission") {
      this.enhanceQuestionCards();
    }

    if (!this.showControlsValue) {
      this.toggleGraphControls(false);
      return;
    }

    const dashboardContainer = document.getElementById("survey-dashboard");
    if (dashboardContainer) {
      if (getComputedStyle(dashboardContainer).position === "static") {
        dashboardContainer.style.position = "relative";
      }

      const existingToggleIcon = dashboardContainer.querySelector('[title="Graph controls enabled"]');
      if (!existingToggleIcon) {
        const toggleIcon = document.createElement("div");
        toggleIcon.innerHTML = `
          <span style="display:inline-block;width:12px;height:12px;border-radius:50%;background:#66FF00;vertical-align:middle;"></span>
          <span style="color:#66FF00;font-weight:light;font-size:12px;vertical-align:middle;">ON</span>
        `;

        const top = this.surveyableTypeValue === "Mission" ? "20px" : "-50px";
        toggleIcon.style.top = top;

        toggleIcon.style.position = "absolute";
        toggleIcon.style.right = "0px";
        toggleIcon.style.cursor = "pointer";
        toggleIcon.style.fontSize = "24px";
        toggleIcon.title = "Graph controls enabled";
        toggleIcon.style.zIndex = "10";

        let controlsVisible = true;

        toggleIcon.addEventListener("click", () => {
          controlsVisible = !controlsVisible;
          this.toggleGraphControls(controlsVisible);

          toggleIcon.innerHTML = controlsVisible
            ? `<span style="display:inline-block;width:12px;height:12px;border-radius:50%;background:#66FF00;vertical-align:middle;"></span>
               <span style="color:#66FF00;font-weight:bold;font-size:12px;vertical-align:middle;">ON</span>`
            : `<span style="display:inline-block;width:12px;height:12px;border-radius:50%;background:#ddd;vertical-align:middle;"></span>
               <span style="color:#ddd;font-weight:light;font-size:12px;vertical-align:middle;">OFF</span>`;

          toggleIcon.title = controlsVisible
            ? "Graph controls enabled"
            : "Graph controls disabled";
        });

        dashboardContainer.appendChild(toggleIcon);
      }
    }
  }

  // Method to set up auto-refresh (periodic updates)
  setupAutoRefresh() {
    if (this.refreshValue === false) {
      console.log("Auto-refresh is disabled");
      return;
    }
    this.refreshTimer = setInterval(async () => {
      console.log("Refreshing survey data...");
      try {
        const surveyData = await this.loadSurveyResults();
        this.allResults = surveyData.results;
        this.applyAttributeFilters();
        this.updateVizPanel();
      } catch (error) {
        console.error("Error during periodic refresh:", error);
      }
    }, this.refreshIntervalValue); // Use the default or passed refresh interval
  }

  disconnect() {
    if (this.refreshTimer) {
      clearInterval(this.refreshTimer);
    }
    // Card #1927 -- a dashboard whose panel was never opened still has a
    // live observer waiting on it, so it has to come down with the rest.
    this.disconnectVisibilityObserver();
    this.disconnectPlotResizeObserver();
    this.destroySlimSelects();
  }

  // Method to fetch survey results
  async loadSurveyResults() {
    try {
      const dateFrom = this.hasDateFromTarget ? this.dateFromTarget.value : "";
      const dateTo = this.hasDateToTarget ? this.dateToTarget.value : "";
      let url = `/api/v1/surveys/results?surveyable_id=${this.surveyableIdValue}&surveyable_type=${this.surveyableTypeValue}&period=${this.periodValue}`;
      if (dateFrom) url += `&date_from=${dateFrom}`;
      if (dateTo) url += `&date_to=${dateTo}`;

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json;charset=UTF-8'
        }
      });

      if (!response.ok) throw new Error("Failed to fetch survey results");

      const data = await response.json();

      // After first load, populate date fields from the API's resolved dates
      if (data.latest_date && this.hasDateToTarget && !this.dateToTarget.value) {
        this.setDatesFromPeriod(data.latest_date);
      }

      return data;
    } catch (error) {
      console.error("Error loading survey:", error);
      throw error;
    }
  }

  setDatesFromPeriod(latestDate) {
    const latest = new Date(latestDate);
    const formatDate = (d) => d.toISOString().split("T")[0];

    if (this.hasDateToTarget) this.dateToTarget.value = formatDate(latest);

    if (this.hasDateFromTarget) {
      let from;
      switch (this.periodValue) {
        case "today":
          from = latest;
          break;
        case "yesterday":
          from = new Date(latest);
          from.setDate(from.getDate() - 1);
          if (this.hasDateToTarget) this.dateToTarget.value = formatDate(from);
          break;
        case "7d":
          from = new Date(latest);
          from.setDate(from.getDate() - 6);
          break;
        case "28d":
          from = new Date(latest);
          from.setDate(from.getDate() - 27);
          break;
        case "all":
          this.dateFromTarget.value = "";
          this.dateToTarget.value = "";
          return;
        default:
          from = latest;
      }
      this.dateFromTarget.value = formatDate(from);
    }
  }

  async periodChanged() {
    const newPeriod = this.periodSelectTarget.value;
    this.periodValue = newPeriod;
    this.writePeriodCookie(newPeriod);

    if (this.hasDateFromTarget) this.dateFromTarget.value = "";
    if (this.hasDateToTarget) this.dateToTarget.value = "";

    await this.reloadDashboard();
  }

  async dateRangeChanged() {
    // When user manually edits dates, switch period to show it's custom
    if (this.hasDateFromTarget && this.hasDateToTarget) {
      if (this.dateFromTarget.value || this.dateToTarget.value) {
        // Only reload if at least one date is set
        this.periodValue = "custom";
        await this.reloadDashboard();
      }
    }
  }

  async reloadDashboard() {
    this.showLoading();

    try {
      const surveyData = await this.loadSurveyResults();
      this.hideError();
      this.clearVizPanel();
      this.survey.fromJSON(surveyData.survey);
      this.allResults = surveyData.results;
      this.resetAttributeFilters();
      this.populateFilterOptions();
      this.surveyResults = this.allResults;
      this.updateResultCount(this.surveyResults.length);

      this.renderVizPanel();

      if (this.surveyableTypeValue === "Mission") {
        this.enhanceQuestionCards();
      }

      this.updateUrlParams();

      this.element.dispatchEvent(new CustomEvent("survey-dashboard:period-changed", {
        bubbles: true,
        detail: { period: this.periodValue }
      }));
    } catch (error) {
      console.error("Failed to reload survey results:", error);
      this.showError();
    } finally {
      this.hideLoading();
    }

    this.applyGridColumns();
    // Card #3648 -- after the grid's column count is settled, not before:
    // resizePlots measures against the container the cards actually ended up in.
    this.resizePlots();
    this.observePlotResizes();
  }

  updateUrlParams() {
    const url = new URL(window.location);
    const params = url.searchParams;

    // Period
    if (this.periodValue && this.periodValue !== "today") {
      params.set("period", this.periodValue);
    } else {
      params.delete("period");
    }

    // Date range
    const dateFrom = this.hasDateFromTarget ? this.dateFromTarget.value : "";
    const dateTo = this.hasDateToTarget ? this.dateToTarget.value : "";
    dateFrom ? params.set("date_from", dateFrom) : params.delete("date_from");
    dateTo ? params.set("date_to", dateTo) : params.delete("date_to");

    // Attribute filters
    const filterParams = [
      { target: "stateFilter", param: "state" },
      { target: "taskerFilter", param: "tasker" },
      { target: "placeOwnerFilter", param: "place_owner" },
      { target: "dayFilter", param: "day" }
    ];
    filterParams.forEach(({ target, param }) => {
      const hasTarget = `has${target.charAt(0).toUpperCase() + target.slice(1)}Target`;
      if (this[hasTarget]) {
        const val = this[`${target}Target`].value;
        val ? params.set(param, val) : params.delete(param);
      }
    });

    window.history.replaceState({}, "", url);
  }

  restoreFromUrlParams() {
    const params = new URLSearchParams(window.location.search);

    const period = params.get("period");
    if (period && this.hasPeriodSelectTarget) {
      const option = this.periodSelectTarget.querySelector(`option[value="${period}"]`);
      if (option) {
        this.periodSelectTarget.value = period;
        this.periodValue = period;
      }
    }

    if (!period && !params.get("date_from") && !params.get("date_to")) {
      const cookiePeriod = this.readPeriodCookie();
      if (cookiePeriod && this.hasPeriodSelectTarget) {
        const option = this.periodSelectTarget.querySelector(`option[value="${cookiePeriod}"]`);
        if (option) {
          this.periodSelectTarget.value = cookiePeriod;
          this.periodValue = cookiePeriod;
        }
      }
    }

    if (params.get("date_from") && this.hasDateFromTarget) this.dateFromTarget.value = params.get("date_from");
    if (params.get("date_to") && this.hasDateToTarget) this.dateToTarget.value = params.get("date_to");
    if (params.get("date_from") || params.get("date_to")) this.periodValue = "custom";

    this._pendingFilterParams = {
      state: params.get("state"),
      tasker: params.get("tasker"),
      place_owner: params.get("place_owner"),
      day: params.get("day")
    };
  }

  async retryLoad() {
    await this.reloadDashboard();
  }

  showError() {
    if (this.hasErrorTarget) this.errorTarget.classList.remove("hidden");
  }

  hideError() {
    if (this.hasErrorTarget) this.errorTarget.classList.add("hidden");
  }

  showLoading() {
    if (this.hasRefreshIconTarget) this.refreshIconTarget.classList.add("animate-spin");
  }

  hideLoading() {
    if (this.hasRefreshIconTarget) this.refreshIconTarget.classList.remove("animate-spin");
  }

  clearVizPanel() {
    if (this.vizPanel) {
      this.vizPanel.clear();
      this.vizPanel = null;
    }
    // Remove SurveyJS Analytics DOM nodes but keep filter bar and spinner
    const container = document.getElementById("survey-dashboard");
    if (container) {
      Array.from(container.children).forEach(child => {
        if (child.hasAttribute("data-survey-dashboard-target") || child.id === "loading-spinner") return;
        if (child.classList.contains("sa-grid") || child.classList.contains("sa-visualizer")) {
          child.remove();
        }
      });
    }
  }

  updateResultCount(count) {
    if (this.hasResultCountTarget && count !== undefined) {
      this.resultCountTarget.textContent = `${count} response${count === 1 ? '' : 's'}`;
    }
  }

  static DAY_ORDER = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"]

  populateFilterOptions() {
    const filters = [
      { target: "stateFilter", field: "_place_state", param: "state" },
      { target: "taskerFilter", field: "_tasker_name", param: "tasker" },
      { target: "placeOwnerFilter", field: "_place_owner_name", param: "place_owner" },
      { target: "dayFilter", field: "_day_of_week", param: "day" }
    ];

    filters.forEach(({ target, field, param }) => {
      const hasTarget = `has${target.charAt(0).toUpperCase() + target.slice(1)}Target`;
      if (!this[hasTarget]) return;

      const select = this[`${target}Target`];
      const currentValue = select.value;
      let values = [...new Set(this.allResults.map(r => r[field]).filter(Boolean))];

      // Sort days in week order, everything else alphabetically
      if (field === "_day_of_week") {
        values.sort((a, b) => this.constructor.DAY_ORDER.indexOf(a) - this.constructor.DAY_ORDER.indexOf(b));
      } else {
        values.sort();
      }

      select.innerHTML = '<option value="">All</option>';
      values.forEach(value => {
        const option = document.createElement("option");
        option.value = value;
        option.textContent = value;
        select.appendChild(option);
      });

      // Restore from URL params on first load, otherwise keep current selection
      const pendingValue = this._pendingFilterParams && this._pendingFilterParams[param];
      if (pendingValue && values.includes(pendingValue)) {
        select.value = pendingValue;
      } else if (currentValue && values.includes(currentValue)) {
        select.value = currentValue;
      }
    });

    // Clear pending params after first use
    this._pendingFilterParams = null;

    // Initialise Slim Select on attribute filter selects
    this.initSlimSelects();
  }

  initSlimSelects() {
    this.destroySlimSelects();
    this._slimInstances = [];
    this._slimInitialising = true;

    // Small delay to ensure DOM is ready, matching the standard slim controller pattern
    setTimeout(() => {
      const targets = ["stateFilter", "taskerFilter", "placeOwnerFilter", "dayFilter"];
      targets.forEach(target => {
        const hasTarget = `has${target.charAt(0).toUpperCase() + target.slice(1)}Target`;
        if (!this[hasTarget]) return;

        const select = this[`${target}Target`];
        const slim = new SlimSelect({
          select: select,
          settings: {
            showSearch: true,
            searchHighlight: true,
            allowDeselect: true,
            closeOnSelect: true,
            placeholderText: "All"
          },
          events: {
            afterChange: () => {
              if (!this._slimInitialising) this.filterChanged();
            }
          }
        });
        this._slimInstances.push(slim);
      });
      this._slimInitialising = false;
    }, 100);
  }

  destroySlimSelects() {
    if (this._slimInstances) {
      this._slimInstances.forEach(slim => slim.destroy());
      this._slimInstances = [];
    }
  }

  filterChanged() {
    this.applyAttributeFilters();
    this.clearVizPanel();
    this.renderVizPanel();
    this.updateUrlParams();

    if (this.surveyableTypeValue === "Mission") {
      this.enhanceQuestionCards();
      this.updateQuestionCounters();
    }

    this.applyGridColumns();
    // Card #3648 -- after the grid's column count is settled, not before:
    // resizePlots measures against the container the cards actually ended up in.
    this.resizePlots();
    this.observePlotResizes();
  }

  applyAttributeFilters() {
    const filters = [
      { target: "stateFilter", field: "_place_state" },
      { target: "taskerFilter", field: "_tasker_name" },
      { target: "placeOwnerFilter", field: "_place_owner_name" },
      { target: "dayFilter", field: "_day_of_week" }
    ];

    let filtered = this.allResults;
    filters.forEach(({ target, field }) => {
      const hasTarget = `has${target.charAt(0).toUpperCase() + target.slice(1)}Target`;
      if (!this[hasTarget]) return;

      const value = this[`${target}Target`].value;
      if (value) {
        filtered = filtered.filter(r => r[field] === value);
      }
    });

    this.surveyResults = filtered;
    this.updateResultCount(this.surveyResults.length);
  }

  async resetFilters(event) {
    if (event) event.preventDefault();

    this.periodSelectTarget.value = "today";
    this.periodValue = "today";
    this.writePeriodCookie("today");

    if (this.hasDateFromTarget) this.dateFromTarget.value = "";
    if (this.hasDateToTarget) this.dateToTarget.value = "";

    this.resetAttributeFilters();

    await this.reloadDashboard();
  }

  writePeriodCookie(period) {
    if (!period || period === "custom") return;
    const expires = new Date(Date.now() + this.constructor.PERIOD_COOKIE_TTL_HOURS * 3600 * 1000).toUTCString();
    document.cookie = `${this.constructor.PERIOD_COOKIE}=${period};expires=${expires};path=/`;
  }

  readPeriodCookie() {
    const match = document.cookie.match(new RegExp(`(?:^|; )${this.constructor.PERIOD_COOKIE}=([^;]*)`));
    return match ? decodeURIComponent(match[1]) : null;
  }

  resetAttributeFilters() {
    ["stateFilter", "taskerFilter", "placeOwnerFilter", "dayFilter"].forEach(target => {
      const hasTarget = `has${target.charAt(0).toUpperCase() + target.slice(1)}Target`;
      if (this[hasTarget]) {
        this[`${target}Target`].value = "";
      }
    });
  }

  // Method to initialize and render the visualization panel
  // Card #3648. Publishes the configured column count as a custom property
  // instead of writing grid-template-columns inline. An inline style beats a
  // media query, so the old inline write made app/assets/stylesheets/
  // survey_js.css's responsive rules unreachable and the grid stayed at three
  // columns all the way down to phone width.
  applyGridColumns() {
    const gridContainer = document.querySelector(".sa-grid");
    if (!gridContainer) return;
    gridContainer.style.removeProperty("grid-template-columns");
    gridContainer.style.setProperty("--sa-grid-cols", this.gridColsValue);
  }

  // Card #3648. Plotly sizes a chart once, from whatever it can measure at
  // creation time, and nothing here puts that right afterwards:
  // renderVizPanel installs a no-op layoutEngine and sets
  // allowDynamicLayout = false, which is precisely survey-analytics' own
  // resize handling being switched off.
  //
  // When Plotly cannot measure the container it falls back to a 700px
  // default, which is how a pie ends up drawn at 700px inside a 250px card,
  // centred on the wrong midpoint and clipped by the card's overflow. The
  // companion min-width: 0 rule in survey_js.css is what lets the graph div
  // shrink back; without it the div's own intrinsic width holds the wrong
  // size in place and Plotly re-measures 700 all over again.
  //
  // Both this call and observePlotResizes below are load bearing, and it is
  // the observer that does most of the work: on a real page the plots are
  // not all laid out by the time loadAndRender reaches this line, so the
  // first-render pass misses them. Ablated on localhost/missions/1819, six of
  // eleven charts sat at 700px inside a 334px card with the observer removed
  // and everything else in place.
  //
  // The staleness test has to read Plotly's own _fullLayout.width, not the
  // div's measured width. The min-width rule above also sets width: 100%, so
  // the div always measures exactly its container while Plotly quietly keeps
  // laying the chart out at 700 -- the div matches, the drawing does not, and
  // a guard comparing the two skips every chart that needs fixing.
  resizePlots() {
    if (typeof Plotly === "undefined") return;
    document.querySelectorAll(".sa-visualizer__content .js-plotly-plot").forEach((plot) => {
      const available = plot.parentElement?.clientWidth;
      if (!available) return;
      const current = plot._fullLayout?.width;
      if (current && Math.abs(current - available) <= 2) return;
      Plotly.Plots.resize(plot);
    });
  }

  // Card #3648. Charts are also mis-sized by anything that changes the
  // container after render -- the browser window, the tab panel being shown
  // for the first time, the grid dropping from three columns to two.
  //
  // Each chart's own wrapper is observed, not the dashboard element. When the
  // grid reflows from three columns to one, the dashboard's box does not
  // change at all -- only the cards inside it do -- so an observer on the
  // dashboard sees nothing and every chart keeps its old width. Found by
  // test/system/missions/dashboard_chart_sizing_test.rb, which was written
  // against an earlier version of this method that made exactly that mistake.
  observePlotResizes() {
    const wrappers = document.querySelectorAll(".sa-visualizer__content");
    if (!wrappers.length) return;
    this.plotResizeObserver ||= new ResizeObserver(() => {
      clearTimeout(this.plotResizeTimer);
      this.plotResizeTimer = setTimeout(() => this.resizePlots(), 100);
    });
    wrappers.forEach((wrapper) => this.plotResizeObserver.observe(wrapper));
  }

  disconnectPlotResizeObserver() {
    clearTimeout(this.plotResizeTimer);
    if (!this.plotResizeObserver) return;
    this.plotResizeObserver.disconnect();
    this.plotResizeObserver = null;
  }

  renderVizPanel() {

    // Custom layout engine to disable SurveyJS Analytics layout engine
    const LayoutEngine = () => ({
      start: () => { },
      stop: () => { },
      update: () => { },
      destroy: () => { }
    });

    this.vizPanelOptions.layoutEngine = LayoutEngine();
    this.vizPanelOptions.allowDynamicLayout = false;

    const questions = this.survey.getAllQuestions();
    // for all types refer to https://surveyjs.io/form-library/documentation/api-reference/question#getType
    const dashboardable_question_types = [
      "dropdown",
      "boolean",
      "checkbox",
      "radiogroup",
      "comment",
      "matrixdropdown",
      "matrix",
      "matrixdynamic",
      "rating",
      "text",
      "tagbox",
      "multipletext",
      "imagepicker"
    ]
    const filteredQuestions = questions.filter(q => dashboardable_question_types.includes(q.getType()));
    const shownfilteredQuestions = filteredQuestions.filter(q => q.hideChart === false);

    const dashboardContainer = document.getElementById("survey-dashboard");
    if (this.vizPanel) {
      return;
    }

    // Create the visualization panel for the first time
    this.vizPanel = new SurveyAnalytics.VisualizationPanel(
      shownfilteredQuestions,
      this.surveyResults,  // Pass the fetched results to the visualization panel
      this.vizPanelOptions
    );

    // Render the panel in the target container
    this.vizPanel.render(dashboardContainer);

    // PROPER SURVEYJS WAY: Set chart types using getVisualizer() and set chartType() AFTER rendering
    shownfilteredQuestions.forEach((question) => {
      const visualizer = this.vizPanel.getVisualizer(question.name);

      if (visualizer) {
        if (question.defaultChartType) {
          visualizer.chartType = question.defaultChartType;
        } else if (question.name.includes('ranging-status') || question.getType() === 'matrixdropdown') {
          visualizer.chartType = "pie";
        } else if (question.name.includes('time-taken-in-mins')) {
          visualizer.chartType = "bullet";
        } else if (question.getType() === 'radiogroup') {
          visualizer.chartType = "pie";
        } else if (question.getType() === 'text' || question.getType() === 'comment' || question.name === 'feedback') {
          visualizer.chartType = "table";
        } else if (question.getType() === 'boolean') {
          visualizer.chartType = "doughnut";
        }
      }
    });

    SurveyAnalytics.PlotlySetup.onPlotCreating.add(function (visualizer, options) {
      if (visualizer.chartType == "matrixdropdown") {
        options.layout.xaxis.autorange = "reversed"
      }
    });


    // Add a simple circle icon to toggle the visibility of question graph controls
    if (dashboardContainer) {
      // Ensure the container is positioned relatively for absolute children
      if (getComputedStyle(dashboardContainer).position === "static") {
        dashboardContainer.style.position = "relative";
      }

      const toggleIcon = document.createElement("div");
      // Simple green circle for "on"
      toggleIcon.innerHTML = `
        <span style="display:inline-block;width:12px;height:12px;border-radius:50%;background:#66FF00;vertical-align:middle;"></span>
        <span style="color:#66FF00;font-weight:light;font-size:12px;vertical-align:middle;">ON</span>
      `;

      const top = this.surveyableTypeValue === "Mission" ? "20px" : "-50px";
      toggleIcon.style.top = top;

      toggleIcon.style.position = "absolute";
      toggleIcon.style.right = "0px";
      toggleIcon.style.cursor = "pointer";
      toggleIcon.style.fontSize = "24px";
      toggleIcon.title = "Graph controls enabled"; // Tooltip for the icon
      toggleIcon.style.zIndex = "10";

      let controlsVisible = true;

      toggleIcon.addEventListener("click", () => {
        controlsVisible = !controlsVisible;
        this.toggleGraphControls(controlsVisible);

        // Update the icon based on visibility: green for on, red for off
        toggleIcon.innerHTML = controlsVisible
          ? `<span style="display:inline-block;width:12px;height:12px;border-radius:50%;background:#66FF00;vertical-align:middle;"></span>
             <span style="color:#66FF00;font-weight:bold;font-size:12px;vertical-align:middle;">ON</span>`
          : `<span style="display:inline-block;width:12px;height:12px;border-radius:50%;background:#ddd;vertical-align:middle;"></span>
             <span style="color:#ddd;font-weight:light;font-size:12px;vertical-align:middle;">OFF</span>`;

        // Update the title based on visibility
        toggleIcon.title = controlsVisible
          ? "Graph controls enabled"
          : "Graph controls disabled";
      });

      dashboardContainer.appendChild(toggleIcon);
    }

    // The ranging-status matrix is far wider than a single card, so it takes
    // the whole row to itself.
    //
    // Card #3648 changed both lines. `span 3` assumed a three column grid and
    // overflowed once the grid drops to two columns under 1280px; `1 / -1`
    // means the full row whatever the column count. The companion
    // `gridRow = "span 2"` went with it: rows are content-sized now
    // (survey_js.css), so forcing two rows just reintroduces the empty half
    // this card set out to remove.
    const saGrid = document.querySelector(".sa-grid");
    const targetDiv = saGrid?.querySelector('[data-question*="ranging-status"]');
    if (targetDiv) {
      targetDiv.style.gridColumn = "1 / -1";
    }

    // Card #2267: survey-analytics' VisualizationMatrixDropdown drops row/product
    // identity when a ranging-status matrixdropdown question has exactly one row --
    // it falls back to a chart renderer keyed only on the I/O/C/X status columns, with
    // no label anywhere. Inject the missing product/SKU label onto those question cards.
    this.addSingleRowRangingStatusLabels(shownfilteredQuestions);

    const footers = document.querySelectorAll('.sa-visualizer__footer');
    footers.forEach(footer => {
      if (footer.innerHTML.trim() === '') {
        footer.classList.add('hidden');
      }
    });

  }

  // Card #2267: a single-row ranging-status matrixdropdown renders as a bare I/O/C/X
  // bar chart with no product/SKU label -- survey-analytics only carries row labels
  // through its multi-row chart renderer. Keep the existing bar chart (consistent with
  // every other ranging-status question) and inject the missing row label as a heading.
  addSingleRowRangingStatusLabels(questions) {
    const singleRowQuestions = questions.filter((question) =>
      question.getType() === "matrixdropdown" &&
      question.name.includes("ranging-status") &&
      Array.isArray(question.rows) &&
      question.rows.length === 1
    );

    singleRowQuestions.forEach((question) => {
      const questionDivs = document.querySelectorAll(`[data-question="${question.name}"]`);

      questionDivs.forEach((questionDiv) => {
        const saQuestionDiv = questionDiv.closest(".sa-question");
        const saQuestionTitle = saQuestionDiv ? saQuestionDiv.querySelector(".sa-question__title") : null;
        if (!saQuestionDiv || !saQuestionTitle) return;

        // Skip if already labelled. Placed as a sibling of (not inside) .sa-question__title
        // so it survives the Mission task-modal title rewrite in handleMissionTasksData(),
        // which clears and replaces the title's innerHTML asynchronously after this runs.
        if (saQuestionDiv.querySelector('[data-single-row-ranging-status-label]')) return;

        const rowText = question.rows[0].text || question.rows[0].value;
        if (!rowText) return;

        const label = document.createElement("div");
        label.setAttribute("data-single-row-ranging-status-label", "");
        label.classList.add("text-gray-600", "text-xs", "font-medium", "mt-1", "px-1");
        label.textContent = rowText.trim();
        saQuestionTitle.insertAdjacentElement("afterend", label);
      });
    });
  }

  // Method to update the visualization panel with new results (without re-rendering)
  updateVizPanel() {
    // Simply update the data in the existing visualization panel
    if (this.vizPanel) {
      this.vizPanel.updateData(
        this.surveyResults
      );
    };
  }

  enhanceQuestionCards() {
    // get all the mission_tasks through the api for this mission_id
    this.fetchMissionTasksData()
      .then(data => {
        this.handleMissionTasksData(data);
      })
      .catch(error => {
        console.error("Error fetching mission tasks data:", error);
        return;
      });
  }

  fetchMissionTasksData() {
    // Fetch mission tasks data from the API
    return fetch(`/api/v1/missions/${this.surveyableIdValue}/tasks`)
      .then(response => response.json())
      .then(data => {
        return data; // Return the fetched data
      })
      .catch(error => {
        console.error("Error fetching mission tasks data:", error);
        return null; // Return null in case of an error
      });
  }

  handleMissionTasksData(data) {
    const questions = document.querySelectorAll('div[data-question]');
    if (!data || !Array.isArray(data)) return;

    questions.forEach(question => {
      const questionName = question.querySelector('h3');
      const matchingTask = data.find(missionTask => missionTask.question_name === question.dataset.question);
      if (matchingTask && matchingTask.id) {
        // Set data attribute for the mission-task-modal controller to find
        question.setAttribute('data-mission-task-id', matchingTask.id);

        // Title opens the mission task modal
        const titleText = questionName.innerText;
        questionName.innerHTML = '';

        const titleSpan = document.createElement('span');
        titleSpan.textContent = titleText;
        titleSpan.classList.add('link', 'cursor-pointer');
        titleSpan.setAttribute('data-mission-task-id', matchingTask.id);
        titleSpan.setAttribute('data-action', 'click->mission-task-modal#open');
        questionName.appendChild(titleSpan);

        // Response counter
        const saQuestionDiv = question.closest('.sa-question');
        const saQuestionTitle = saQuestionDiv.querySelector('.sa-question__title');
        if (saQuestionDiv && !saQuestionDiv.querySelector(`#responses-counter-${matchingTask.id}`)) {
          const counter = document.createElement('span');
          counter.id = `responses-counter-${matchingTask.id}`;
          const filteredCount = this.surveyResults.filter(r => r[question.dataset.question] != null && r[question.dataset.question] !== "").length;
          counter.textContent = `${filteredCount} response${filteredCount === 1 ? '' : 's'}`;
          counter.classList.add('text-gray-500', 'text-xs', 'italic', 'font-light');
          counter.style.display = 'block';
          saQuestionTitle.appendChild(counter);
        }
      }
    });
  }

  updateQuestionCounters() {
    const counters = document.querySelectorAll('[id^="responses-counter-"]');
    counters.forEach(counter => {
      // Walk up to find the question name from the data-question attribute
      const questionDiv = counter.closest('.sa-question')?.parentElement;
      if (!questionDiv) return;
      const questionName = questionDiv.dataset.question;
      if (!questionName) return;

      // Count how many filtered results have a non-null value for this question
      const count = this.surveyResults.filter(r => r[questionName] != null && r[questionName] !== "").length;
      counter.textContent = `${count} response${count === 1 ? '' : 's'}`;
    });
  }

  removeMainSvgBackgroundColorStyle() {
    // Find all SVG elements with class "main-svg" and set their background to transparent
    const mainSvgs = document.querySelectorAll("svg.main-svg");
    mainSvgs.forEach(svg => {
      svg.style.background = "transparent";
    });
  }

  toggleGraphControls(controlsVisible) {
    const toolbars = document.querySelectorAll(".sa-visualizer__toolbar");
    toolbars.forEach(toolbar => {
      toolbar.style.display = controlsVisible ? "" : "none";
    });

    const counters = document.querySelectorAll('[id^="responses-counter-"]');
    counters.forEach(counter => {
      counter.style.display = controlsVisible ? "block" : "none";
    });

    if (controlsVisible) {
      this.setupAutoRefresh();
    } else {
      if (this.refreshTimer) {
        clearInterval(this.refreshTimer);
        console.log("Auto-refresh disabled");
      }
    }
  }
}
